import {doNothing} from '@raison/toolbox';
import React from 'react';
import {shallowEqual} from 'react-redux';
import {Modals} from './modalsApi';

type ModalThunk = AppThunkAction<void>;
const noopClose = () => doNothing;

export type DialogRegistration = {
    dialogName: string;
    render: (state: Modals.WithModalsSlice) => React.JSX.Element | null;
    close: () => ModalThunk;
    implicitClose: () => ModalThunk;
};

type EmptyToVoid<T> = T | ({} extends T ? void : T);

export namespace DialogRegistration {
    interface Params<P extends object> {
        name: string;
        Component: (props: P) => React.JSX.Element;
    }

    export function create<P extends object>({name, Component}: Params<P>) {
        const handle = Modals.create<P>(name);
        const select = Modals.select(handle);
        const selectIsOpen = Modals.selectIsOpen(handle);
        const boundActions = Modals.bindActions(handle);

        type closeFn = () => ModalThunk;
        const close: closeFn = () => dispatch => {
            dispatch(boundActions.close());
        };

        type openFn = (p: EmptyToVoid<P>) => ModalThunk;
        const open: openFn = props => (dispatch, getState) => {
            const [isOpen, currentProps] = select(getState());
            if (isOpen && shallowEqual(props, currentProps)) {
                return;
            }
            dispatch(boundActions.open(props as P));
        };

        const matchClose = boundActions.close.match;
        const matchOpen = boundActions.open.match;

        const Dialog = Object.assign(Component, {displayName: name});

        const registration: DialogRegistration = {
            dialogName: name,
            render(state) {
                const [isOpen, props] = select(state);
                if (isOpen) {
                    return <Dialog {...props} />;
                } else {
                    return null;
                }
            },
            close,
            implicitClose: close,
        };

        return {
            ...registration,
            id: handle.id,
            Dialog,
            matchClose,
            matchOpen,
            selectIsOpen,
            select,
            open,
        };
    }

    /**
     * Creates a dialog that cannot be close by clicking backdrop or close button, but still can be closed explicitly with a thunk
     * @todo might need a better name
     */
    export function createNonClosable<P extends object>(params: Params<P>) {
        return {
            ...create(params),
            implicitClose: noopClose,
        } satisfies ReturnType<typeof DialogRegistration.create<P>>;
    }
}
